import { Grid, Theme, Typography, Button, Switch, FormControlLabel } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { AppRole, EmailPreferences, MutationStatus } from '../gql-types.generated';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flexFlow: 'nowrap',
    },
    headerContainer: {
      marginBottom: theme.spacing(2),
    },
    chipContainer: {
      display: 'flex',
    },
    switchContainer: {
      display: 'flex',
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(0.5),
    },
    helperText: {
      marginTop: theme.spacing(1),
    },
    statusIcon: {
      marginRight: theme.spacing(0.5),
    },
    buttonAction: {
      margin: theme.spacing(1),
    },
    name: {
      letterSpacing: 1.5,
    },
    header: {
      maxWidth: 'calc(100% - 76px)',
    },
    editContainer: {
      width: '76px',
      marginRight: theme.spacing(1),
    },
  }),
);

interface EmailReceiptPreferencesProps {
  networkBusy: boolean;
  viewerAppRole: AppRole | undefined;
  emailPreferences?: EmailPreferences | null;
  update: (sendPayerReceipts: boolean, sendMerchantReceipts: boolean) => void;
  requestInFlight: boolean;
  status?: MutationStatus;
  clearStatus: () => void;
}

const EmailReceiptPreferences: React.FC<EmailReceiptPreferencesProps> = props => {
  const classes = useStyles();
  const { viewerAppRole, emailPreferences, update, networkBusy, requestInFlight, status, clearStatus } = props;

  const canUserEdit = viewerAppRole === AppRole.Admin;
  const inLoadingState = requestInFlight || networkBusy;

  // Note: The API uses "disable" flags, but we display as "send" (enabled) to the user
  // So we invert the values for display
  const [editEnabled, setEditEnabled] = useState<boolean>(false);
  const [sendPayerReceipts, setSendPayerReceipts] = useState<boolean>(!emailPreferences?.disablePayerReceipt);
  const [sendMerchantReceipts, setSendMerchantReceipts] = useState<boolean>(!emailPreferences?.disableMerchantReceipt);

  const handleModifyClick = () => {
    if (canUserEdit) {
      setEditEnabled(!editEnabled);
    }
  };

  const resetValues = () => {
    // Reset to saved state (inverted from disable flags)
    setSendPayerReceipts(!emailPreferences?.disablePayerReceipt);
    setSendMerchantReceipts(!emailPreferences?.disableMerchantReceipt);
    clearStatus();
    setEditEnabled(false);
  };

  const handleSave = () => {
    // Pass the "send" values - the action will convert them to "disable" flags
    update(sendPayerReceipts, sendMerchantReceipts);
  };

  // Check if values have changed from the original
  const hasChanges =
    sendPayerReceipts !== !emailPreferences?.disablePayerReceipt || sendMerchantReceipts !== !emailPreferences?.disableMerchantReceipt;

  const getFeatureChip = (isEnabled: boolean) =>
    isEnabled ? (
      <>
        <DoneIcon className={classes.statusIcon} fontSize="small" color="success" />
        <Typography variant="body1">Enabled</Typography>
      </>
    ) : (
      <>
        <CloseIcon className={classes.statusIcon} fontSize="small" color="error" />
        <Typography variant="body1">Disabled</Typography>
      </>
    );

  useEffect(() => {
    if (status && !status?.error) {
      clearStatus();
      setEditEnabled(false);
    }
  }, [status]);

  useEffect(() => {
    // Update local state when emailPreferences changes
    setSendPayerReceipts(!emailPreferences?.disablePayerReceipt);
    setSendMerchantReceipts(!emailPreferences?.disableMerchantReceipt);
  }, [emailPreferences]);

  return (
    <Grid container className={classes.root} data-cy="email-receipt-preferences" aria-label="Email Receipt Preferences" role="region">
      <Grid
        container
        item
        xs={12}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={classes.headerContainer}
      >
        <Typography variant="subtitle" className={classes.header}>
          Email Receipts
        </Typography>
        {canUserEdit && !editEnabled && (
          <div className={classes.editContainer}>
            <Button
              startIcon={<EditIcon />}
              onClick={handleModifyClick}
              disabled={inLoadingState}
              size="small"
              color="primary"
              variant="outlined"
              aria-label="Edit email receipt preferences"
            >
              EDIT
            </Button>
          </div>
        )}
        <Grid item className={classes.helperText} xs={12}>
          <Typography variant="body1">
            Control what receipts are sent when a payment is completed. To make changes, click Edit, then toggle the appropriate
            settings.
          </Typography>
        </Grid>
      </Grid>
      <Grid container item xs={12} md={10} lg={9} spacing={2} justifyContent="center">
        {/* Send Payer Receipts */}
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="button" className={classes.name}>
            Send Payer Receipts
          </Typography>
        </Grid>
        <Grid container item xs={12} sm={6} lg={8}>
          <Grid className={classes.switchContainer} item xs={12}>
            {!editEnabled && (
              <Grid className={classes.chipContainer} item xs={12}>
                {getFeatureChip(sendPayerReceipts)}
              </Grid>
            )}
            {editEnabled && (
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    onChange={() => setSendPayerReceipts(!sendPayerReceipts)}
                    checked={sendPayerReceipts}
                    disabled={inLoadingState}
                    size="small"
                    inputProps={{ 'aria-label': 'Send payer receipts switch' }}
                  />
                }
                label={<Typography variant="body1">Enabled</Typography>}
              />
            )}
          </Grid>
        </Grid>

        {/* Send Merchant Receipts */}
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="button" className={classes.name}>
            Send Merchant Receipts
          </Typography>
        </Grid>
        <Grid container item xs={12} sm={6} lg={8}>
          <Grid className={classes.switchContainer} item xs={12}>
            {!editEnabled && (
              <Grid className={classes.chipContainer} item xs={12}>
                {getFeatureChip(sendMerchantReceipts)}
              </Grid>
            )}
            {editEnabled && (
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    onChange={() => setSendMerchantReceipts(!sendMerchantReceipts)}
                    checked={sendMerchantReceipts}
                    disabled={inLoadingState}
                    size="small"
                    inputProps={{ 'aria-label': 'Send merchant receipts switch' }}
                  />
                }
                label={<Typography variant="body1">Enabled</Typography>}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      {/* Save / Cancel */}
      {editEnabled && (
        <Grid container item xs={12} justifyContent="flex-end">
          <Button size="small" className={classes.buttonAction} color="primary" onClick={resetValues} disabled={inLoadingState}>
            Cancel
          </Button>
          <Button
            size="small"
            className={classes.buttonAction}
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={inLoadingState || !hasChanges}
          >
            Save
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default EmailReceiptPreferences;
